// Port of the ECPI System Validation logic (sysChk.js and the network evaluation in the
// speedtest.ecpi.net page). Thresholds and messages are kept identical to the original.

export type CheckStatus = "Pass" | "Alert" | "Fail" | "";

export interface CheckResult
{
    key: string;
    label: string;
    value: string;
    status: CheckStatus;
    messages?: string[];
    infoUrl?: string;
    hidden?: boolean;
}

const INFO_BASE = "https://speedtest.ecpi.net/info.html";
const faq = (anchor: string) => `${INFO_BASE}#${anchor}`;

const fail = (key: string, label: string, value: string, messages: string[], anchor: string): CheckResult =>
    ({ key, label, value, status: "Fail", messages, infoUrl: faq(anchor) });
const alert = (key: string, label: string, value: string, messages: string[], anchor: string): CheckResult =>
    ({ key, label, value, status: "Alert", messages, infoUrl: faq(anchor) });
const pass = (key: string, label: string, value: string): CheckResult =>
    ({ key, label, value, status: "Pass" });

// Mirrors JS loose comparison of a string against a number ("abc" >= 10 -> false)
const num = (v: string) => Number(v);

const trimVersion = (v: string) =>
{
    let i: number;
    if ((i = v.indexOf(";")) != -1) v = v.substring(0, i);
    if ((i = v.indexOf(" ")) != -1) v = v.substring(0, i);
    if ((i = v.indexOf(")")) != -1) v = v.substring(0, i);
    return v;
};

export function checkMemory(): CheckResult
{
    const label = "Installed Memory";
    const ram = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    const value = String(ram);
    if (num(value) >= 8) return pass("ram", label, "≥8 GB RAM");
    if (num(value) >= 6)
        return alert("ram", label, `${value} GB RAM`,
            ["OK for accessing ECPI web resources.", "CIS and EET curriculum require at least 16 GB RAM."], "faqHardware");
    if (num(value) >= 0)
        return fail("ram", label, `${value} GB RAM`,
            ["Unsupported amount of installed memory detected.", "Please use a system with more memory (RAM)."], "faqHardware");
    return { key: "ram", label, value: "", status: "", hidden: true };
}

export function checkCookies(): CheckResult
{
    const label = "Cookies Enabled?";
    return navigator.cookieEnabled
        ? pass("cookies", label, "Yes")
        : fail("cookies", label, "No", ["Disabled cookies detected.", "Please enable cookies for ECPI websites."], "faqCookies");
}

export async function checkWebcam(): Promise<CheckResult>
{
    const label = "Webcam Detected?";
    let hasWebcam = false;
    const md = navigator.mediaDevices;
    if (md && md.enumerateDevices)
    {
        try
        {
            const devices = await md.enumerateDevices();
            hasWebcam = devices.some(d => d.kind === "videoinput");
        } catch
        {
            hasWebcam = false;
        }
    }
    return hasWebcam
        ? pass("cam", label, "Yes")
        : fail("cam", label, "No", ["Unable to detect an installed webcam.", "A webcam is required for remote learning."], "faqHardware");
}

export function checkOperatingSystem(): CheckResult[]
{
    const ua = navigator.userAgent;
    const typeLabel = "OS Type";
    const verLabel = "OS Version";
    let os: string;
    let osVer = "Unknown";
    let osType: CheckResult;
    let t: number;

    const partial = ["Partially supported OS type detected.", "Please use a fully supported operating system."];
    const windowsOnly = ["OK for accessing ECPI web resources.", "CIS, EET, and HS curriculum require a Windows system."];

    if ((t = ua.indexOf("Windows NT")) != -1)
    {
        os = "Windows";
        osVer = ua.substring(t + 11);
        osType = pass("os", typeLabel, os);
    } else if ((t = ua.indexOf("CPU OS")) != -1)
    {
        os = "iOS (iPad)";
        osVer = ua.substring(t + 7).replace(/_/g, ".");
        osType = alert("os", typeLabel, os, windowsOnly, "faqOS");
    } else if ((t = ua.indexOf("CPU iPhone OS")) != -1)
    {
        os = "iOS (iPhone)";
        osVer = ua.substring(t + 14).replace(/_/g, ".");
        osType = alert("os", typeLabel, os, partial, "faqOS");
    } else if ((t = ua.indexOf("Android")) != -1)
    {
        os = "Android";
        osVer = ua.substring(t + 8);
        osType = alert("os", typeLabel, os, partial, "faqOS");
    } else if ((t = ua.indexOf("Intel Mac OS X")) != -1)
    {
        os = "macOS (Apple)";
        osVer = ua.substring(t + 15).replace(/_/g, ".");
        osType = alert("os", typeLabel, os, windowsOnly, "faqOS");
    } else if ((t = ua.indexOf("CrOS")) != -1)
    {
        os = "Chrome OS (X11)";
        osVer = ua.substring(t + 12);
        osType = fail("os", typeLabel, os, ["Unsupported OS type detected.", "Please use a supported operating system."], "faqOS");
    } else if ((t = ua.indexOf("Linux")) != -1)
    {
        os = "Linux";
        osVer = ua.substring(t + 6);
        osType = alert("os", typeLabel, os, partial, "faqOS");
    } else
    {
        os = "Unknown";
        osType = fail("os", typeLabel, os, ["Unable to determine OS type.", "Please use a supported operating system."], "faqOS");
    }

    osVer = trimVersion(osVer);

    const verFail = () => fail("osVer", verLabel, osVer,
        ["Unsupported OS version detected.", "Please update your operating system."], "faqOS");
    let osVersion: CheckResult;

    if (os == "Windows")
        osVersion = num(osVer.substring(0, 2)) >= 10 ? pass("osVer", verLabel, osVer) : verFail();
    else if (os == "iOS (iPad)" || os == "iOS (iPhone)")
        osVersion = num(osVer.substring(0, 4).split(".").join("")) >= 140 ? pass("osVer", verLabel, osVer) : verFail();
    else if (os == "Android")
        osVersion = num(osVer.substring(0, 2).split(".").join("")) >= 8 ? pass("osVer", verLabel, osVer) : verFail();
    else if (os == "macOS (Apple)")
        osVersion = num(osVer.substring(0, 5).split(".").join("")) >= 1014 ? pass("osVer", verLabel, osVer) : verFail();
    else if (os == "Linux")
        osVersion = { key: "osVer", label: verLabel, value: osVer, status: "Alert" };
    else if (os == "Unknown")
        osVersion = fail("osVer", verLabel, osVer,
            ["Unable to determine OS version.", "Please use a supported operating system."], "faqOS");
    else
        osVersion = { key: "osVer", label: verLabel, value: osVer, status: "Fail" };

    return [osType, osVersion];
}

export function checkBrowser(): CheckResult[]
{
    const ua = navigator.userAgent;
    const nameLabel = "Browser Type";
    const verLabel = "Browser Version";
    const unsupported = ["Unsupported browser type detected.", "Please use a supported web browser."];

    let name = navigator.appName;
    let ver = "" + parseFloat(navigator.appVersion);
    let nameStatus: CheckStatus = "";
    let verStatus: CheckStatus = "";
    let nameMsgs: string[] | undefined;
    let verMsgs: string[] | undefined;
    let t: number;
    let n: number;

    const markUnsupported = () =>
    {
        nameStatus = "Fail";
        nameMsgs = unsupported;
        verStatus = "Fail";
    };

    if ((t = ua.indexOf("Opera")) != -1)
    {
        name = "Opera";
        ver = ua.substring(t + 6);
        markUnsupported();
        if ((t = ua.indexOf("Version")) != -1) ver = ua.substring(t + 8);
    } else if ((t = ua.indexOf("OPR")) != -1)
    {
        name = "Opera";
        ver = ua.substring(t + 4);
        markUnsupported();
    } else if ((t = ua.indexOf("Edge")) != -1)
    {
        name = "Microsoft Edge";
        ver = ua.substring(t + 5);
        nameStatus = "Pass";
    } else if ((t = ua.indexOf("EdgiOS")) != -1)
    {
        name = "Microsoft Edge (Mobile)";
        ver = ua.substring(t + 7);
        markUnsupported();
    } else if ((t = ua.indexOf("EdgA")) != -1)
    {
        name = "Microsoft Edge (Mobile)";
        ver = ua.substring(t + 5);
        markUnsupported();
    } else if ((t = ua.indexOf("Edg")) != -1)
    {
        name = "Microsoft Edge";
        ver = ua.substring(t + 4);
        nameStatus = "Pass";
    } else if ((t = ua.indexOf("Firefox")) != -1)
    {
        name = "Mozilla Firefox";
        ver = ua.substring(t + 8);
        nameStatus = "Pass";
    } else if ((t = ua.indexOf("rv:")) != -1)
    {
        name = "Internet Explorer";
        ver = ua.substring(t + 3);
        markUnsupported();
    } else if ((t = ua.indexOf("MSIE")) != -1)
    {
        name = "Internet Explorer";
        ver = ua.substring(t + 5);
        markUnsupported();
    } else if ((t = ua.indexOf("Chrome")) != -1)
    {
        name = "Google Chrome";
        ver = ua.substring(t + 7);
        nameStatus = "Pass";
    } else if ((t = ua.indexOf("CriOS")) != -1)
    {
        name = "Google Chrome (Mobile)";
        ver = ua.substring(t + 6);
        nameStatus = "Pass";
    } else if ((t = ua.indexOf("FxiOS")) != -1)
    {
        name = "Mozilla Firefox (Mobile)";
        ver = ua.substring(t + 6);
        markUnsupported();
    } else if ((t = ua.indexOf("Safari")) != -1)
    {
        name = "Apple Safari";
        ver = ua.substring(t + 7);
        nameStatus = "Pass";
        if ((t = ua.indexOf("Version")) != -1) ver = ua.substring(t + 8);
    } else if ((n = ua.lastIndexOf(" ") + 1) < (t = ua.lastIndexOf("/")))
    {
        name = ua.substring(n, t);
        ver = ua.substring(t + 1);
        markUnsupported();
        if (name.toLowerCase() == name.toUpperCase()) name = navigator.appName;
    }

    ver = trimVersion(ver);

    const minVersions: Record<string, [number, number]> = {
        "Google Chrome": [3, 122],
        "Google Chrome (Mobile)": [3, 122],
        "Apple Safari": [2, 16],
        "Microsoft Edge": [3, 121],
        "Mozilla Firefox": [3, 123]
    };
    const rule = minVersions[name];
    if (rule)
    {
        if (num(ver.substring(0, rule[0])) >= rule[1]) verStatus = "Pass";
        else
        {
            verStatus = "Fail";
            verMsgs = ["Unsupported browser version detected.", "Please update your web browser."];
        }
    }

    return [
        {
            key: "browName", label: nameLabel, value: name, status: nameStatus,
            messages: nameMsgs, infoUrl: nameMsgs ? faq("faqBrowser") : undefined
        },
        {
            key: "browVer", label: verLabel, value: ver, status: verStatus,
            messages: verMsgs, infoUrl: verMsgs ? faq("faqBrowser") : undefined
        }
    ];
}

export interface NetworkMeasurements
{
    clientIp: string;
    download: string;
    upload: string;
    pingSamples: number[];
    jitterSamples: number[];
}

const average = (values: number[]) =>
    values.length ? Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(0)) : NaN;

export function splitClientIp(clientIp: string): { ip: string; isp: string }
{
    const idx = clientIp.indexOf(" - ");
    if (idx !== -1) return { ip: clientIp.substring(0, idx), isp: clientIp.substring(idx + 3) };
    // The ECPI backend sometimes returns only the ISP name (no "<ip> - " prefix)
    const looksLikeIp = /^[\d.]+$|^[0-9a-f:]+$/i.test(clientIp.trim());
    return looksLikeIp ? { ip: clientIp, isp: "" } : { ip: "", isp: clientIp };
}

export function evaluateNetwork(m: NetworkMeasurements): CheckResult[]
{
    const { ip, isp } = splitClientIp(m.clientIp);
    const results: CheckResult[] = [];

    results.push(pass("ip", "IP Address", ip || "Undetermined"));

    results.push(isp == "" || isp.startsWith("Unknown ISP") || isp.startsWith("Lookup Failed")
        ? alert("isp", "Service Provider", isp || "Undetermined",
            ["Unable to determine internet service provider.", "Are you behind a military or corporate firewall?"], "faqNetwork")
        : pass("isp", "Service Provider", isp));

    const dl = num(m.download);
    const dlLabel = "Download Speed";
    const dlValue = `${m.download} Mbps`;
    if (dl >= 10) results.push(pass("dl", dlLabel, dlValue));
    else if (dl >= 2) results.push(alert("dl", dlLabel, dlValue,
        ["Low download speed detected.", "A broadband internet connection is recommended."], "faqNetwork"));
    else if (dl >= 0) results.push(fail("dl", dlLabel, dlValue,
        ["Unsupported download speed detected.", "Please use a faster internet connection."], "faqNetwork"));
    else results.push({ key: "dl", label: dlLabel, value: dlValue, status: "" });

    const ul = num(m.upload);
    const ulLabel = "Upload Speed";
    const ulValue = `${m.upload} Mbps`;
    if (ul >= 3) results.push(pass("ul", ulLabel, ulValue));
    else if (ul >= 1) results.push(alert("ul", ulLabel, ulValue,
        ["Low upload speed detected.", "A broadband internet connection is recommended."], "faqNetwork"));
    else if (ul >= 0) results.push(fail("ul", ulLabel, ulValue,
        ["Unsupported upload speed detected.", "Please use a faster internet connection."], "faqNetwork"));
    else results.push({ key: "ul", label: ulLabel, value: ulValue, status: "" });

    const ping = average(m.pingSamples);
    const pingLabel = "Ping (average)";
    const pingValue = isNaN(ping) ? "" : `${ping} ms`;
    if (ping < 100) results.push(pass("ping", pingLabel, pingValue));
    else if (ping < 250) results.push(alert("ping", pingLabel, pingValue,
        ["High ping latency detected.", "Please attempt to stabilize your internet connection."], "faqNetwork"));
    else if (ping >= 250) results.push(fail("ping", pingLabel, pingValue,
        ["Unsupported ping latency detected.", "Please attempt to stabilize your internet connection."], "faqNetwork"));
    else results.push({ key: "ping", label: pingLabel, value: pingValue, status: "" });

    const jit = average(m.jitterSamples);
    const jitLabel = "Jitter (average)";
    const jitValue = isNaN(jit) ? "" : `${jit} ms`;
    if (jit < 25) results.push(pass("jit", jitLabel, jitValue));
    else if (jit < 50) results.push(alert("jit", jitLabel, jitValue,
        ["High jitter latency detected.", "Please attempt to stabilize your internet connection."], "faqNetwork"));
    else if (jit >= 50) results.push(fail("jit", jitLabel, jitValue,
        ["Unsupported jitter latency detected.", "Please attempt to stabilize your internet connection."], "faqNetwork"));
    else results.push({ key: "jit", label: jitLabel, value: jitValue, status: "" });

    return results;
}
